import React, { useState, useEffect } from 'react';
import { X, ChevronDown, Award, Plus } from 'react-feather';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { athleteAPI } from '../services/api';
import { useResponsive } from '../hooks/useResponsive';
import Modal from './Modal';
import { TextInput } from './ui';

/**
 * AwardsAndHonorsEditModal
 *
 * Dedicated modal for athletic awards + academic achievements. The Awards &
 * Honors card on PlayerProfile shows both lists side-by-side; previously the
 * only way to add either was to dig into the (unrelated) Athletic Info or
 * Academic Info edit modals. Moved here so users can find it where it lives.
 *
 * Data model (unchanged):
 *   - athletic awards → athlete_sport_info.awards (jsonb array)
 *   - academic achievements → athlete_academic_achievements (one row each,
 *     is_active soft-delete on replace)
 *
 * Both are persisted in a single transaction via POST /athlete/awards-and-honors.
 */

interface Props {
  isOpen: boolean;
  onClose: () => void;
  athleteId: string;
  initialAwards: string[];
  initialAchievements: string[];
  onUpdate: (next: { awards: string[]; achievements: string[] }) => void;
  /** Render inside the unified ProfileEditModal (no own overlay). */
  embedded?: boolean;
  /** Called after a successful save. Falls back to onClose. */
  onSaved?: () => void;
}

const AwardsAndHonorsEditModal: React.FC<Props> = ({
  isOpen,
  onClose,
  athleteId,
  initialAwards,
  initialAchievements,
  onUpdate,
  embedded = false,
  onSaved,
}) => {
  const { sessionToken } = useAuth();
  const toast = useToast();
  const { isMobile } = useResponsive();

  const [awards, setAwards] = useState<string[]>([]);
  const [newAward, setNewAward] = useState('');
  const [achievements, setAchievements] = useState<string[]>([]);
  const [newAchievement, setNewAchievement] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setAwards(initialAwards || []);
      setAchievements(initialAchievements || []);
      setNewAward('');
      setNewAchievement('');
      setHasChanges(false);
    }
  }, [isOpen, initialAwards, initialAchievements]);

  const addAward = () => {
    const t = newAward.trim();
    if (!t) return;
    setAwards([...awards, t]);
    setNewAward('');
    setHasChanges(true);
  };
  const removeAward = (idx: number) => { setAwards(awards.filter((_, i) => i !== idx)); setHasChanges(true); };
  const addAchievement = () => {
    const t = newAchievement.trim();
    if (!t) return;
    setAchievements([...achievements, t]);
    setNewAchievement('');
    setHasChanges(true);
  };
  const removeAchievement = (idx: number) => {
    setAchievements(achievements.filter((_, i) => i !== idx));
    setHasChanges(true);
  };

  const handleSave = async () => {
    if (!sessionToken || !athleteId) return;
    // #240 — don't silently drop text typed into the add-input but not yet
    // "added": fold the pending entries into the payload on Save.
    const finalAwards = newAward.trim() ? [...awards, newAward.trim()] : awards;
    const finalAchievements = newAchievement.trim() ? [...achievements, newAchievement.trim()] : achievements;
    setIsSaving(true);
    try {
      const res = await athleteAPI.updateAwardsAndHonors(sessionToken, athleteId, {
        athletic_awards: finalAwards,
        academic_achievements: finalAchievements,
      });
      if (res.success) {
        toast.success('Awards and honors saved.');
        onUpdate({ awards: res.athletic_awards, achievements: res.academic_achievements });
        (onSaved || onClose)();
      } else {
        toast.error('Failed to save awards and honors');
      }
    } catch (err) {
      console.error('Save awards/honors error:', err);
      toast.error('Failed to save awards and honors');
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen) return null;

  const sectionBox: React.CSSProperties = {
    background: '#F9FAFB',
    border: '1px solid #E5E7EB',
    borderRadius: '12px',
    padding: '20px',
  };
  const sectionHeader: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    marginBottom: '16px',
  };
  const sectionIcon: React.CSSProperties = {
    width: 36,
    height: 36,
    borderRadius: 18,
    background: 'white',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 12,
  };
  const inputRow: React.CSSProperties = { display: 'flex', gap: 8, marginBottom: 12 };
  /* No `outline` here, deliberately: an inline declaration outranks the global
   * `:focus-visible` ring in styles/globals.css whatever its specificity, and
   * nothing replaces it, so `outline: none` left both add-item fields with no
   * focus indicator at all. */
  const inputStyle: React.CSSProperties = {
    /* width:auto because TextInput is width:100% by default and this box shares
     * a row with the round Add button — flex, not width, should size it. */
    width: 'auto',
    flex: 1,
    height: 40,
    border: '1px solid #D1D5DB',
    borderRadius: 8,
    padding: '0 12px',
    fontSize: 14,
    color: '#1F2937',
  };
  const addBtnStyle: React.CSSProperties = {
    width: 40,
    height: 40,
    background: '#F9550D',
    border: 'none',
    borderRadius: 8,
    cursor: 'pointer',
    fontSize: 20,
    fontWeight: 600,
    color: 'white',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  };
  const itemRow: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 12,
    background: 'white',
    border: '1px solid #E5E7EB',
    borderRadius: 8,
  };
  const removeBtnStyle: React.CSSProperties = {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: 4,
    display: 'flex',
    alignItems: 'center',
    color: '#DC2626',
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      inline={embedded}
      isDirty={hasChanges}
      labelledById="awards-title"
      contentStyle={{
        backgroundColor: 'white',
        borderRadius: 16,
        width: isMobile ? 'calc(100vw - 32px)' : '90%',
        maxWidth: isMobile ? 'calc(100vw - 32px)' : 700,
        maxHeight: isMobile ? 'calc(100vh - 100px)' : '90vh',
        display: 'flex',
        flexDirection: 'column',
        overflowY: 'hidden',
        fontFamily: 'Manrope, sans-serif',
      }}
    >
        {/* Header */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '20px 24px',
            borderBottom: '1px solid #E5E7EB',
          }}
        >
          <h2 id="awards-title" style={{ fontSize: 20, fontWeight: 600, color: '#1F2937', margin: 0 }}>
            Edit Awards &amp; Honors
          </h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: 8,
              display: 'flex',
              alignItems: 'center',
              color: '#6B7280',
            }}
          >
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div
          style={{
            padding: '20px 24px',
            overflowY: 'auto',
            display: 'flex',
            flexDirection: 'column',
            gap: 16,
          }}
        >
          {/* Athletic Awards */}
          <div style={sectionBox}>
            <div style={sectionHeader}>
              <div style={sectionIcon}>
                <Award size={18} color="#F9550D" strokeWidth={2} />
              </div>
              <div style={{ flex: 1 }}>
                <h3
                  style={{
                    fontSize: 16,
                    fontWeight: 600,
                    color: '#1F2937',
                    margin: 0,
                    marginBottom: 2,
                  }}
                >
                  Athletic Awards
                </h3>
                <p style={{ fontSize: 13, color: '#6B7280', margin: 0 }}>
                  All-Conference, All-State, MVP, tournament wins, etc.
                </p>
              </div>
            </div>

            <div style={inputRow}>
              <TextInput
                type="text"
                value={newAward}
                onChange={(e) => { setNewAward(e.target.value); setHasChanges(true); }}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addAward();
                  }
                }}
                placeholder="Add athletic award..."
                aria-label="Athletic award"
                style={inputStyle}
              />
              <button onClick={addAward} type="button" aria-label="Add athletic award" style={addBtnStyle}>
                <Plus size={20} />
              </button>
            </div>

            {awards.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {awards.map((a, i) => (
                  <div key={i} style={itemRow}>
                    <span style={{ fontSize: 14, color: '#1F2937', flex: 1 }}>{a}</span>
                    <button onClick={() => removeAward(i)} type="button" style={removeBtnStyle}>
                      <X size={16} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Academic Achievements */}
          <div style={sectionBox}>
            <div style={sectionHeader}>
              <div style={sectionIcon}>
                <ChevronDown size={18} color="#F9550D" strokeWidth={2} />
              </div>
              <div style={{ flex: 1 }}>
                <h3
                  style={{
                    fontSize: 16,
                    fontWeight: 600,
                    color: '#1F2937',
                    margin: 0,
                    marginBottom: 2,
                  }}
                >
                  Academic Achievements
                </h3>
                <p style={{ fontSize: 13, color: '#6B7280', margin: 0 }}>
                  Honor roll, National Honor Society, AP scholar, scholarships, etc.
                </p>
              </div>
            </div>

            <div style={inputRow}>
              <TextInput
                type="text"
                value={newAchievement}
                onChange={(e) => { setNewAchievement(e.target.value); setHasChanges(true); }}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addAchievement();
                  }
                }}
                placeholder="Add academic achievement..."
                aria-label="Academic achievement"
                style={inputStyle}
              />
              <button onClick={addAchievement} type="button" aria-label="Add academic achievement" style={addBtnStyle}>
                <Plus size={20} />
              </button>
            </div>

            {achievements.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {achievements.map((a, i) => (
                  <div key={i} style={itemRow}>
                    <span style={{ fontSize: 14, color: '#1F2937', flex: 1 }}>{a}</span>
                    <button
                      onClick={() => removeAchievement(i)}
                      type="button"
                      style={removeBtnStyle}
                    >
                      <X size={16} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div
          style={{
            display: 'flex',
            gap: 12,
            padding: '16px 24px',
            borderTop: '1px solid #E5E7EB',
          }}
        >
          <button
            onClick={onClose}
            disabled={isSaving}
            style={{
              flex: 1,
              padding: 12,
              fontSize: 14,
              fontWeight: 600,
              color: '#374151',
              background: 'white',
              border: '1px solid #D1D5DB',
              borderRadius: 8,
              cursor: isSaving ? 'not-allowed' : 'pointer',
              opacity: isSaving ? 0.5 : 1,
              fontFamily: 'Manrope, sans-serif',
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={isSaving}
            style={{
              flex: 1,
              padding: 12,
              fontSize: 14,
              fontWeight: 700,
              color: 'white',
              background: '#F9550D',
              border: 'none',
              borderRadius: 8,
              cursor: isSaving ? 'not-allowed' : 'pointer',
              opacity: isSaving ? 0.7 : 1,
              fontFamily: 'Manrope, sans-serif',
              boxShadow: '0 4px 12px rgba(249, 85, 13, 0.3)',
            }}
          >
            {isSaving ? 'Saving...' : 'Save'}
          </button>
        </div>
    </Modal>
  );
};

export default AwardsAndHonorsEditModal;
